import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useCall } from "@/context/CallContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, Phone, ChatCircleText, Users } from "@phosphor-icons/react";

export default function Directory() {
  const [q, setQ] = useState("");
  const call = useCall();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["pbx-directory", q],
    queryFn: async () => (await api.get("/pbx/directory", { params: q ? { q } : {} })).data,
    keepPreviousData: true,
  });

  const results = data?.results || [];

  const ring = (r) => {
    if (!r.callable) { toast("This extension isn't linked to a member yet."); return; }
    call?.startCall?.(r.user_id, false);
    toast(`Calling ${r.name} · ext ${r.ext}`);
  };

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Company Directory</h1>
        <p className="mt-2 text-muted-foreground">Search by name, department or extension. One tap to call a colleague in-app — free.</p>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-full border border-border bg-card py-2.5 pl-10 pr-4 text-sm outline-none focus:border-primary"
          placeholder="Search “billing”, “10301”, a name…" />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : results.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <Users size={28} className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">No matching extensions</p>
          <p className="mt-1 text-xs text-muted-foreground">Extensions your admin creates for your organization appear here.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((r) => (
            <div key={r.ext} data-testid={`dir-card-${r.ext}`} className="flex items-center justify-between rounded-2xl border border-border bg-card p-4">
              <div className="min-w-0">
                <p className="truncate font-medium">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.department_label}</p>
                <p className="mt-1 flex items-center gap-1.5 font-mono-metric text-sm font-semibold text-primary">
                  <span className={`h-1.5 w-1.5 rounded-full ${r.online ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                  {r.ext}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {r.callable && (
                  <button data-testid={`dir-msg-${r.ext}`} onClick={() => navigate(`/app/text?with=${r.user_id}`)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:text-foreground">
                    <ChatCircleText size={18} />
                  </button>
                )}
                <button data-testid={`dir-call-${r.ext}`} onClick={() => ring(r)} disabled={!r.callable}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105 disabled:opacity-40">
                  <Phone size={18} weight="fill" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
